import { SuspenseSelect } from '@/components/Suspense';
import {
  BaseSelectProps,
  useSetCacheQueryData,
} from '@/context/SelectComponent/Base';
import { useModelsToOptionsWithCacheItems } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { getPageData } from '@/utils/getPageData';
import {
  FormSelect,
  FormSelectProps,
  SelectProps,
} from '@moneyforward/ap-frontend-components';
import {
  Excises as ExcisesModel,
  useGetExcisesSuspenseInfinite,
} from 'ap-openapi';
import { memo, startTransition, useCallback, useMemo, useState } from 'react';
import { FieldValues } from 'react-hook-form';

export type Props<TFieldValues extends FieldValues> = {
  selectProps?: Omit<
    FormSelectProps<TFieldValues>['selectProps'],
    'options' | 'comboBox'
  >;
  query?: string;
  per?: number;
  validateMaxCount?: number;
  comboBox?: boolean;
  setLabelField?: (label: string) => void;
} & Omit<FormSelectProps<TFieldValues>, 'selectProps' | 'label'> &
  BaseSelectProps;

const _ExciseSelect = <TFieldValues extends FieldValues>({
  selectProps: _selectProps,
  defaultOptions: _defaultOptions,
  onChangeAfter,
  setLabelField,
  cacheQueryKey,
  per = 100,
  comboBox = true,
  ...rest
}: Props<TFieldValues>) => {
  const defaultOptions = useMemo(
    () => _defaultOptions ?? [],
    [_defaultOptions]
  );
  const { t } = useTranslation();
  const [query, setQuery] = useState<string | undefined>(undefined);
  const {
    data: apiData,
    error,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useGetExcisesSuspenseInfinite(
    {
      query,
      per: per.toString(),
    },
    {
      query: {
        getNextPageParam: (page) => {
          const url = new URL(page.data.next ?? '/', location.href);
          if (!url.searchParams.has('page')) return undefined;
          return url.searchParams.get('page');
        },
      },
    }
  );
  if (error) throw error;
  const data = useMemo(() => {
    return getPageData(apiData, 'excises');
  }, [apiData]);
  const options = useModelsToOptionsWithCacheItems<ExcisesModel>(
    data,
    cacheQueryKey,
    query,
    defaultOptions,
    { labelKeys: ['long_name'], value: 'id' }
  );

  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) {
        await fetchNextPage();
      }
      callback();
    },
    [fetchNextPage, hasNextPage]
  );
  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);
  const selectProps: SelectProps = useMemo(() => {
    return {
      onSearchDebounceWait: 600,
      comboBox: comboBox,
      placeholder: t('excise_placeholder'),
      inputPlaceholder: t('excise_input_placeholder'),
      ..._selectProps,
      options: options,
      fetchMore: fetchMore,
      onSearch: onSearch,
      loading: isLoading,
      mode: 'single',
      selectAll: undefined,
      selectAllLabel: undefined,
    };
  }, [comboBox, t, _selectProps, options, fetchMore, onSearch, isLoading]);

  useSetCacheQueryData(cacheQueryKey, data);

  const wrapOnChangeAfter = useCallback(
    (value: string | number | (string | number)[]) => {
      const selectedOption = options.find(
        (option) => option.value === String(value)
      );
      onChangeAfter?.(value);
      setLabelField?.(selectedOption?.label ?? '');
    },
    [setLabelField, options, onChangeAfter]
  );

  return (
    <FormSelect<TFieldValues>
      selectProps={selectProps}
      {...rest}
      onChangeAfter={wrapOnChangeAfter}
    />
  );
};
_ExciseSelect.displayName = '_ExciseSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ExciseSelect = memo((props) => (
  <SuspenseSelect>
    <_ExciseSelect {...props} />
  </SuspenseSelect>
)) as typeof _ExciseSelect;
ExciseSelect.displayName = 'ExciseSelect';
